import { defaultStringifySearch } from '@tanstack/react-router'

const isPrimitive = (value: unknown) =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'

/**
 * Serializes list search params as repeated keys (`?ids=a&ids=b`) instead of
 * the router default, which JSON-encodes them into a single value. Repeated
 * keys are what `URLSearchParams` and server frameworks understand, so links
 * stay constructible outside the app. The default parser already collapses
 * repeated keys back into an array, so only stringification needs overriding.
 * Lists holding anything other than primitives keep the JSON encoding.
 */
export function stringifySearch(search: Record<string, unknown>): string {
  const repeatable: [string, unknown[]][] = []
  const rest: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(search ?? {})) {
    if (Array.isArray(value) && value.every(isPrimitive)) {
      repeatable.push([key, value])
    } else {
      rest[key] = value
    }
  }

  const params = new URLSearchParams(defaultStringifySearch(rest))
  for (const [key, values] of repeatable) {
    for (const value of values) {
      params.append(key, String(value))
    }
  }

  const query = params.toString()
  return query ? `?${query}` : ''
}

/**
 * Normalizes a repeated search param into a string list. A repeated key
 * (`?ids=a&ids=b`) parses to an array, but a single occurrence (`?ids=a`)
 * parses to a bare string — both are the same list to a caller.
 */
export function toStringList(value: unknown): string[] {
  if (typeof value === 'string') return [value]
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string')
}
